import { useStudioMediaMode } from '../studio/use-studio-media-mode';
import { useHeldMode } from '../studio/use-held-mode';
import { HeldModeBody } from '../studio/HeldModeBody';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import type { UserAvatar } from '../../api/people';
import { uiText } from '../../i18n';
import { studioApi, studioCheapOffer, studioExpectedPrice, studioFields, studioUnitPrice, type StudioApp } from '../studio/studio-api';
import { useStudioDocument } from '../studio/use-studio-document';
import { useStudioForm } from '../studio/use-studio-form';
import { priceUnit, record, rows } from '../studio/studio-content';
import { PhotoFieldInput } from '../photo/PhotoFieldInput';
import { PhotoStyles } from '../photo/PhotoStyles';
import { photoThemeOrder } from '../photo/photo-model';
import { PhotoPeople } from '../photo/PhotoPeople';
import { PhotoResultImport } from '../photo/PhotoResultImport';
import { StudioMediaHeader, StudioUnitPrice } from '../studio/StudioMediaHeader';
import { VideoSource } from './VideoSource';
import { VideoShooting } from './VideoShooting';
import { VideoOrderResult } from './VideoOrderResult';
import { VideoProductTray } from './VideoProductTray';
import { StudioSettings, StudioCreateButton, StudioInfoRow, StudioSettingsSection, type StudioSettingSlot } from '../studio/StudioFormParts';
import { shootCells, useShootDicts } from '../studio/use-studio-shoot';
import { StudioReferenceCard, StudioReferencePlate } from '../studio/StudioReference';
import { useStudioReference } from '../studio/use-studio-reference';
import { useStudioPhotoPreviews } from '../studio/use-studio-photo-previews';
import { uploadExample } from '../studio/StudioUploadExample';
import { videoMissing, videoPolicy, videoPhotoPolicy, videoTool, videoLabels, videoValues } from './video-model';
import '../photo/server-photo.css';
import '../studio/native-template.css';
interface Props {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  headerActive?: boolean;
}
export function VideoOrderForm(props: Props) {
  const { active, seed, switching: busy, error, switchMode } = useStudioMediaMode(props.app, props.initialInputs, 'video');
  const held = useHeldMode(active, seed);
  const [lastFileApp, setLastFileApp] = useState(props.app.id);
  const screen = useStudioDocument(signal => api.request<Record<string, unknown>>('/app/studio/screens/video', { signal }), 'video-tools');
  const tools = rows(record(screen.data?.video).tools).flatMap(v => {
    const tool = videoTool(v);
    return tool ? [tool] : [];
  });
  const styleTool = tools.find(tool => tool.mode === 'style');
  const styleCatalog = useStudioDocument(
    signal => (styleTool ? studioApi.form(styleTool.app_id, signal) : Promise.resolve(null)),
    `video-style-catalog:${styleTool?.app_id ?? 0}`,
  );
  return (
    <section className="server-photo server-video native-studio-form">
      {screen.error && (
        <p role="alert">
          {uiText('Не удалось загрузить режимы видео.')}{' '}
          <button type="button" onClick={screen.reload}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {error && <p role="alert">{uiText(error)}</p>}
      {held.bodies.map(body => (
        <HeldModeBody key={body.app.id} body={body} lock={busy || held.holding}>
          <VideoFormBody
            app={body.app}
            avatar={props.avatar}
            initialInputs={body.seed}
            headerActive={props.headerActive && body.visible}
            tools={tools}
            toolsReady={!!screen.data}
            themeOrder={photoThemeOrder(screen.data, 'video')}
            styleForm={styleCatalog.data ?? null}
            preferredFileId={lastFileApp}
            resultActions={record(screen.data?.video).result_actions}
            switching={false}
            onReady={held.ready}
            onSwitch={(id, inputs, expectedOwner, transfer) => {
              if (tools.find(tool => tool.app_id === active.id)?.mode !== 'text') setLastFileApp(active.id);
              return switchMode(id, inputs, expectedOwner, transfer);
            }}
          />
        </HeldModeBody>
      ))}
    </section>
  );
}
/** Пояснения строк «Формата» видео (§3, таблица «Что в рамке») */
const formatNotes: Record<string, string> = {
  src: 'Без обрезки, как снято',
  auto: 'Без обрезки, как снято',
  '9:16': 'Для Reels, Shorts и сторис',
  '16:9': 'Для YouTube и экрана',
  '1:1': 'Для аватарки и ленты',
  '4:5': 'Для ленты',
  '3:4': 'Как снимок с телефона',
};
const formatNames: Record<string, string> = { '1:1': 'Квадрат', '3:4': 'Портрет', '4:5': 'Пост', '9:16': 'Сторис', '16:9': 'Горизонтальное' };
const SOUND_TIP =
  'Видео будет со звуком: модель сама сгенерирует фон, шумы и музыку по сюжету. Речь можно добавить после создания: «Добавить речь»';
const CLIP_TIP = 'В работу идёт до 10 секунд ролика. Из длинного выберите фрагмент под рамкой';
const PRODUCT_TIP = 'Формат задаёт инструмент: портрет 3:4, ролик на 5 секунд';
/** Чипы описания ролика (VCHIPS шаблона, assets/bot-photo.js): подпись и полный текст. ph — движение фото, vd — добавки к видео со стилем, edit — правки видео */
const motionChips = [
  ['Улыбка', 'Мягко улыбается и моргает'],
  ['Машет рукой', 'Машет рукой в камеру'],
  ['Ветер', 'Лёгкий ветер в волосах и одежде'],
] as const;
const tweakChips = [
  ['Плавнее', 'Сделай движения плавнее'],
  ['Ярче', 'Добавь яркости и контраста'],
] as const;
const changeChips = [
  ['Сменить фон', 'Замени фон на ночной город'],
  ['День в ночь', 'Сделай вечер с огнями'],
  ['Снег', 'Добавь падающий снег'],
  ['Убрать лишнее', 'Убери людей на заднем плане'],
  ['Очки', 'Добавь солнцезащитные очки'],
] as const;
/** Подсказки поля описания по виду (K.place видео, assets/bot-photo.js) */
const PLACE = {
  tx: 'Что происходит в ролике? Например: камера медленно летит над ночным городом, огни и мокрый асфальт',
  ph: 'Как двигаться? Например: улыбается и машет рукой, ветер в волосах',
  edit: 'Что поменять в видео? Например: замени фон на ночной город, добавь снег',
  vd: 'Что добавить? Например: движения плавнее, ярче',
};
const SPECIAL = [
  'photo',
  'photo_2',
  'photo_3',
  'photo_4',
  'photo_5',
  'main_photo',
  'motion',
  'video',
  'style',
  'reference',
  'shoot',
  'start_ms',
  'end_ms',
  'crop_x',
  'crop_y',
  'zoom',
  'format',
  'duration',
  'audio',
  'description',
];
function VideoFormBody({
  app,
  avatar,
  initialInputs,
  tools,
  toolsReady = false,
  themeOrder,
  resultActions,
  switching,
  onSwitch,
  headerActive,
  styleForm,
  preferredFileId,
  onReady,
}: Props & {
  tools: NonNullable<ReturnType<typeof videoTool>>[];
  /** Документ экрана с режимами пришёл: до него шаблон от режима не отличить */
  toolsReady?: boolean;
  /** Порядок тем «Всех стилей» из документа экрана (video.themes) */
  themeOrder?: string[];
  styleForm: import('../studio/studio-api').StudioForm | null;
  preferredFileId: number;
  resultActions?: unknown;
  switching: boolean;
  onSwitch(id: number, inputs: Record<string, string>, owner?: number, transfer?: boolean): void;
  /** Форма режима пришла (или не пришла): тело можно показать вместо прежнего */
  onReady?(appId: number): void;
}) {
  const baseState = useStudioForm({ app, avatar, initialInputs });
  const formReady = !!baseState.form || !!baseState.error;
  useEffect(() => {
    if (formReady) onReady?.(app.id);
  }, [formReady, onReady, app.id]);
  const state = switching ? { ...baseState, locked: true } : baseState;
  const { form, inputs, orderId, intent, busy, locked, submissionBlocked, rights, error } = state;
  const policy = form ? videoPolicy(form) : null,
    shared = form ? videoPhotoPolicy(form) : null;
  const [source, selectSource] = useState<'photo' | 'video'>(initialInputs?.video ? 'video' : 'photo');
  const [queuedFile, setQueuedFile] = useState<File | null>(null);
  const selectedSource = inputs.video ? 'video' : source;
  const missing = form ? videoMissing(form, inputs, rights, state.uploadIds ?? {}) : null;
  const fields = studioFields(form);
  const field = (key: string) => fields.find(item => item.key === key);
  const description = field('description');
  const mode = policy?.mode;
  const clip = !!inputs.video || mode === 'edit';
  const descriptionOnForm = mode === 'text' || mode === 'edit';
  const descriptionLabel =
    mode === 'text' ? 'Описание' : mode === 'edit' ? 'Что поменять' : mode === 'style' && inputs.video ? 'Что изменить' : 'Как двигаться';
  const chips = mode === 'text' ? undefined : mode === 'edit' ? changeChips : mode === 'style' && inputs.video ? tweakChips : motionChips;
  const placeholder = PLACE[mode === 'text' ? 'tx' : mode === 'edit' ? 'edit' : mode === 'style' && inputs.video ? 'vd' : 'ph'];
  /* Шаблон каталога, а не режим экрана: в шапке его название, без сегмента режимов, без «Без стиля» и без перехода в режимы
     (title() и noneOk() шаблона, bot-photo.js). Режимы — из документа экрана: пока он не пришёл или режимов в нём нет, шаблон не отличить. */
  const isTemplate = toolsReady && tools.length > 0 && !tools.some(tool => tool.app_id === app.id);
  const fallbackRail = mode === 'photo' && styleForm ? videoPhotoPolicy(styleForm) : null;
  const rail = shared?.styles.length ? shared : isTemplate ? null : fallbackRail;
  const styleTool = tools.find(tool => tool.mode === 'style');
  const uploads = form?.fields.filter(f => ['photo', 'video'].includes(f.key) && f.upload) ?? [];
  const sourceFields = mode === 'style' ? uploads.filter(f => f.key === selectedSource) : uploads;
  const shoot = useShootDicts(!!record(form).shoot || !!record(record(form).settings).shoot);
  const referenceField = mode === 'product' ? field('reference') : undefined;
  const referencePreview = useStudioPhotoPreviews(state, referenceField ? [referenceField] : []);
  const reference = useStudioReference({ state, field: referenceField, previewUrl: referencePreview.urls.reference });
  // Цена единицы прайса важнее цены карточки каталога (оформление; на проде демо-0).
  const unitPrice = studioUnitPrice(form) ?? app.price ?? form?.expected_price;
  /* Только показ: цена шаблона без единицы «за секунду» — за запуск («60 C»); у режимов экрана — за секунду.
     Единица — unit, без него — per, как у сервера (MessagingStudioItemNormalizer.Duration) */
  const unitTotal = isTemplate && priceUnit(app.price) !== 'per_second';
  const format = field('format'),
    duration = field('duration');
  const price = state.price ?? (form ? studioExpectedPrice(form, inputs) : 0);
  /* «от» — только пока рамка ждёт ролик (priceFrom шаблона: src()==='vd' && !S.clip) и цена зависит от его длины (форма платная) */
  const from =
    !!form?.chargeable &&
    !inputs.video &&
    !inputs.photo &&
    uploads.some(f => f.key === 'video') &&
    (mode === 'edit' || (mode === 'style' && selectedSource === 'video'));
  const sound = mode !== 'parallax' && !clip;
  // В карточке только ячейки спецификации (§3): служебные поля сервера (mode, preset, resolution) идут умолчанием.
  const extra = fields.filter(
    f => !SPECIAL.includes(f.key) && !/^cast_|^action_/.test(f.key) && !f.upload && !!videoLabels[f.key] && f.key !== 'resolution',
  );
  const formatValue = (value: string | undefined) =>
    !value || ['src', 'auto'].includes(value) ? (clip ? 'Как у видео' : 'Как у фото') : (formatNames[value] ?? videoValues[value] ?? value);
  const text = (inputs.description ?? '').trim().replace(/\s+/g, ' ');
  const slots: StudioSettingSlot[] = [
    ...(mode === 'product'
      ? [{ title: 'Формат', value: 'Портрет', kind: 'info' as const, lock: true, tip: PRODUCT_TIP }]
      : format && format.values.length > 1
        ? [{ title: 'Формат', value: formatValue(inputs.format), section: 'format', kind: 'choice' as const }]
        : []),
    ...(mode === 'product'
      ? []
      : clip
        ? [{ title: 'Длительность', value: 'до 10 сек', kind: 'info' as const, lock: true, tip: CLIP_TIP }]
        : duration && duration.values.length > 1
          ? [{ title: 'Длительность', value: `${inputs.duration || duration.values[0]} сек`, section: 'duration', kind: 'choice' as const }]
          : []),
    ...shootCells(form, inputs, shoot.data),
    ...(sound ? [{ title: 'Звук', value: 'От модели', kind: 'info' as const, tip: SOUND_TIP }] : []),
    ...extra
      .filter(item => item.values.length)
      .map(item => ({
        title: videoLabels[item.key],
        value: videoValues[inputs[item.key]] ?? (inputs[item.key] || 'По умолчанию'),
        section: item.key,
      })),
    ...(referenceField
      ? [
          {
            title: 'Свой референс',
            value: reference.active ? 'Картинка сцены' : '',
            empty: !reference.active,
            kind: 'files' as const,
            thumb: reference.url ? <img src={reference.url} alt="" /> : undefined,
            onTap: () => reference.press(),
          },
        ]
      : []),
    ...(description && !descriptionOnForm
      ? [
          {
            title: descriptionLabel,
            value: text,
            empty: !text,
            kind: 'text' as const,
            section: 'description',
            wide: Array.from(text).length > 18,
          },
        ]
      : []),
  ];
  const descriptionInput = (inSheet: boolean) =>
    description ? (
      <PhotoFieldInput
        label={descriptionLabel}
        field={description}
        state={state}
        avatar={avatar}
        presets={chips}
        chipMode={descriptionOnForm ? 'set' : 'add'}
        hint={inSheet ? 'необязательно' : undefined}
        note={inSheet ? '' : undefined}
        placeholder={placeholder}
      />
    ) : null;
  return (
    <>
      <StudioMediaHeader
        kind="video"
        title={isTemplate || (policy && ['product', 'parallax'].includes(policy.mode)) ? app.name : 'Видео'}
        price={unitPrice}
        total={unitTotal}
        mode={mode === 'text' ? 'text' : 'file'}
        enabled={headerActive}
        showModes={!orderId && !!policy && !isTemplate && !['product', 'parallax'].includes(policy.mode)}
        unit={mode === 'text' || mode === 'product'}
        locked={locked || switching || !state.ownerId}
        fileAvailable={tools.some(tool => tool.mode === 'style' || tool.mode === 'photo')}
        textAvailable={tools.some(tool => tool.mode === 'text')}
        onMode={next => {
          const tool =
            next === 'text'
              ? tools.find(tool => tool.mode === 'text')
              : (tools.find(tool => tool.app_id === preferredFileId && !['text', 'product', 'parallax'].includes(tool.mode)) ??
                tools.find(tool => tool.mode === 'style') ??
                tools.find(tool => tool.mode === 'photo'));
          if (tool) onSwitch(tool.app_id, inputs, state.ownerId);
        }}
      />
      {!form && !error && <p role="status">{uiText('Загружаем…')}</p>}
      {error && (
        <p role="alert">
          {uiText(error)}{' '}
          {!intent && (
            <button type="button" onClick={() => state.setRevision(v => v + 1)}>
              {uiText('Обновить')}
            </button>
          )}
        </p>
      )}
      {form && !orderId && (
        <form
          onSubmit={e => {
            e.preventDefault();
            if (!busy && !switching && !submissionBlocked && (intent || (!missing && form.available))) void state.submit();
          }}
        >
          <PhotoResultImport state={state} />
          {mode === 'product' ? (
            <VideoProductTray state={state} />
          ) : (
            sourceFields.map(item => (
              <VideoSource
                key={item.key}
                field={item}
                state={state}
                badge={
                  <span className="bc-pb pb-corner">
                    <StudioUnitPrice kind="video" price={unitPrice} total={unitTotal} />
                  </span>
                }
                example={mode === 'parallax' ? uploadExample(policy?.example) : null}
                mediaFields={mode === 'style' ? uploads : undefined}
                queuedFile={queuedFile}
                onQueuedFileConsumed={() => setQueuedFile(null)}
                onMediaFile={(target, file) => {
                  state.clearUpload(target.key === 'photo' ? 'video' : 'photo');
                  selectSource(target.key === 'video' ? 'video' : 'photo');
                  state.setInputs(current => {
                    const next = { ...current };
                    for (const key of ['start_ms', 'end_ms', 'crop_x', 'crop_y', 'zoom', 'shoot']) delete next[key];
                    return next;
                  });
                  setQueuedFile(file);
                }}
              />
            ))
          )}
          {descriptionOnForm && descriptionInput(false)}
          {rail && (
            <PhotoStyles
              state={state}
              policy={rail}
              allowNone={!isTemplate}
              unit="video"
              unitPrice={unitPrice}
              unitTotal={unitTotal}
              themeOrder={themeOrder}
              selectedId={!isTemplate && (mode === 'photo' || mode === 'edit') ? 'none' : undefined}
              initialReferenceOpen={initialInputs?.open_reference === 'true'}
              onReferenceOpen={
                !isTemplate && fallbackRail && styleTool
                  ? () => onSwitch(styleTool.app_id, { ...inputs, open_reference: 'true', style: 'none' }, state.ownerId, true)
                  : undefined
              }
              onSelectStyle={
                /* шаблон: стиль ставится на месте выбором ленты (рисованный гасит только строки «Съёмки» экрана) */
                isTemplate
                  ? undefined
                  : (mode === 'photo' || mode === 'edit') && styleTool
                    ? id => {
                        if (id !== 'none') onSwitch(styleTool.app_id, { ...inputs, style: id }, state.ownerId, true);
                      }
                    : mode === 'style'
                      ? id => {
                          if (id === 'none') {
                            const tool = tools.find(tool => tool.mode === (selectedSource === 'video' ? 'edit' : 'photo'));
                            if (tool) onSwitch(tool.app_id, { ...inputs, style: 'none' }, state.ownerId, true);
                          } else
                            state.setInputs(current => {
                              const next: Record<string, string> = { ...current, style: id };
                              delete next.reference;
                              return next;
                            });
                        }
                      : undefined
              }
            />
          )}
          {rail && !isTemplate && (mode === 'photo' || mode === 'edit') && (
            <p className="bc-cap">
              <span>{uiText(mode === 'edit' ? 'Видео меняется по вашему описанию' : 'Фото оживает как есть, без стиля')}</span>
            </p>
          )}
          {mode === 'text' && shared && <PhotoPeople state={state} policy={shared} avatar={avatar} />}
          {!!slots.length && (
            <StudioSettings disabled={locked} slots={slots}>
              {format && mode !== 'product' && format.values.length > 1 && (
                <StudioSettingsSection id="format">
                  <PhotoFieldInput
                    label={videoLabels.format}
                    field={format}
                    state={state}
                    avatar={avatar}
                    valueLabels={{ ...videoValues, ...formatNames, src: formatValue('src'), auto: formatValue('auto') }}
                    descriptions={formatNotes}
                  />
                </StudioSettingsSection>
              )}
              {mode === 'product' && <StudioInfoRow title="Формат" value="Портрет" tip={PRODUCT_TIP} lock />}
              {duration && mode !== 'product' && !clip && duration.values.length > 1 && (
                <StudioSettingsSection id="duration">
                  <PhotoFieldInput label={videoLabels.duration} field={duration} state={state} avatar={avatar} valueLabels={videoValues} />
                </StudioSettingsSection>
              )}
              {clip && mode !== 'product' && <StudioInfoRow title="Длительность" value="до 10 сек" tip={CLIP_TIP} lock />}
              <VideoShooting state={state} />
              {sound && <StudioInfoRow title="Звук" value="От модели" tip={SOUND_TIP} />}
              {extra.map(item => (
                <StudioSettingsSection key={item.key} id={item.key}>
                  <PhotoFieldInput label={videoLabels[item.key]} valueLabels={videoValues} field={item} state={state} avatar={avatar} />
                </StudioSettingsSection>
              ))}
              {referenceField && (
                <StudioSettingsSection id="reference" title="Свой референс" kind="files">
                  <StudioReferenceCard reference={reference} disabled={locked} label="Загрузить фото" />
                  <StudioReferencePlate reference={reference} use={referenceField.upload?.use ?? 'scene'} />
                </StudioSettingsSection>
              )}
              {description && !descriptionOnForm && (
                <StudioSettingsSection id="description" kind="text">
                  {descriptionInput(true)}
                </StudioSettingsSection>
              )}
              {inputs.video && <p>{uiText('Звук исходника не сохраняется: модель создаёт новый звук.')}</p>}
            </StudioSettings>
          )}
          {referenceField && reference.hidden}
          {!form.available && <p role="status">{uiText('Создание сейчас недоступно')}</p>}
          <StudioCreateButton
            kind="video"
            price={price}
            from={from}
            busy={busy || switching}
            uploading={state.uploadingFile}
            blocked={submissionBlocked}
            pending={!!intent}
            missing={missing}
            available={form.available}
            balance={state.balance}
            cheap={studioCheapOffer(form)}
            onCheap={() => void state.submit({ cheap: true })}
            onCreate={() => void state.submit()}
          />
        </form>
      )}
      {!form && intent && !orderId && (
        <button disabled={busy || submissionBlocked} onClick={() => void state.submit()}>
          {uiText('Продолжить проверку')}
        </button>
      )}
      <VideoOrderResult state={state} resultActions={resultActions} />
    </>
  );
}
